/**
 * Tooltip — thin styled wrapper around Radix Tooltip primitives. We
 * use it on the left icon rail so the tiny glyphs get a label without
 * pulling in the OS-rendered `title=""` chrome (which arrives ~700ms
 * after hover and looks foreign next to the mid-century surfaces).
 *
 * The TooltipProvider is mounted once near the root; callers compose
 *
 *   <Tooltip>
 *     <TooltipTrigger asChild><button>…</button></TooltipTrigger>
 *     <TooltipContent side="right">Label</TooltipContent>
 *   </Tooltip>
 *
 * Default `delayDuration` is 120ms — close enough to "almost
 * immediately" without being annoying when the cursor only grazes the
 * icon on the way to something else.
 */

import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/lib/utils';

export const TooltipProvider = ({
  delayDuration = 120,
  skipDelayDuration = 60,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) => (
  <TooltipPrimitive.Provider
    delayDuration={delayDuration}
    skipDelayDuration={skipDelayDuration}
    {...props}
  />
);

export const Tooltip = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;

export const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 8, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 select-none rounded-md border-[1.5px] border-charcoal bg-charcoal text-cream',
        'px-2.5 py-1 font-mono text-[11px] tracking-wide shadow-[0_4px_12px_-4px_rgba(20,20,20,0.35)]',
        'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
        'data-[side=right]:slide-in-from-left-1 data-[side=left]:slide-in-from-right-1',
        'data-[side=top]:slide-in-from-bottom-1 data-[side=bottom]:slide-in-from-top-1',
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = 'TooltipContent';
